import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Sparkles, RotateCw } from 'lucide-react';
import { Button, Text, Input, Notice } from '../ui';
import { usePostModalStore } from '../../store/usePostModalStore';

const RegeneratePostForm = ({ post, onRegenerateSubmit, showProductImage = false, onCancel }) => {
  const { info, setInfo } = usePostModalStore();

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      userInput: '',
    },
  });

  const userInput = watch('userInput');

  const onSubmit = async (data) => {
    if (!data.userInput.trim()) return;
    setInfo(null); // Clear any existing info messages

    await onRegenerateSubmit(data.userInput);
    reset(); // Clear form after successful submission
  };

  const isRegenerating = post?.status === 'regeneration' || isSubmitting;
  const isPublished = post?.status === 'published';

  return (
    <div className='w-full h-full flex flex-col overflow-hidden '>
      {/* Header */}
      <div className='flex items-center gap-2 mb-4'>
        <div className='w-6 h-6 flex items-center justify-center'>
          <Sparkles className='w-5 h-5' style={{ color: 'var(--brand)' }} />
        </div>
        <Text variant='caption' className='font-bold tracking-tight uppercase mb-0' style={{ color: 'var(--text-primary)' }}>
          Regenerate Image
        </Text>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col px-2 flex-1 overflow-y-auto justify-between'
      >
        <div className='space-y-6'>
          {/* Description */}
          <Text variant='caption' className='' style={{ color: 'var(--text-body)' }}>
            Enter a detailed prompt to regenerate a new image that better matches your needs.
          </Text>

          {/* Show product image if post type is product and showProductImage is true */}
          {post.data?.imageUrl && showProductImage && (
            <div className='space-y-2'>
              <Text variant='caption' className='mb-0' style={{ color: 'var(--text-body)' }}>
                Product Image:
              </Text>
              <div className='w-full rounded-lg overflow-hidden' style={{ border: '2px solid var(--border)', backgroundColor: 'var(--bg-surface)' }}>
                <img
                  src={post.data.imageUrl}
                  alt='Product'
                  className='w-full h-auto object-contain max-h-48'
                />
              </div>
              <Text variant='caption' className='italic' style={{ color: 'var(--text-muted)' }}>
                💡 Reference for regeneration
              </Text>
            </div>
          )}

          {/* Textarea */}
          <div className='flex flex-col flex-1 mb-6'>
            <Controller
              name='userInput'
              control={control}
              rules={{
                required: 'Please provide instructions',
                minLength: {
                  value: 3,
                  message: 'Instructions must be at least 3 characters',
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type='textarea'
                  placeholder="Describe the image you want to create... e.g., 'A modern home office setup with natural sunlight, a laptop on a wooden desk, and a cup of coffee.'"
                  disabled={isRegenerating || isPublished}
                  rows={8}
                />
              )}
            />
            {errors.userInput && (
              <Text variant='caption' className='text-red-500 mt-2'>
                {errors.userInput.message}
              </Text>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className='flex flex-col items-center gap-3 px-2'>
          {info && (
            <Notice
              variant='warning'
              message={info.message}
              actionText={info.actionText}
              onActionClick={() => {
                if (info.actionLink) {
                  window.open(info.actionLink, '_blank');
                }
              }}
              className={'w-full'}
            />
          )}
          <Button
            type='submit'
            disabled={
              isRegenerating || isPublished || !userInput?.trim() || userInput.trim().length < 3
            }
            variant='primary'
            className='w-full py-3 text-base font-medium group'
            icon={RotateCw}
            additionalIconClasses='w-5 h-5 mr-2 group-disabled:rotate-0 group-hover:rotate-180 transition-transform duration-500'
            iconPosition='left'
            loading={isRegenerating}
          >
            {isRegenerating ? 'Regenerating...' : 'Regenerate Image'}
          </Button>
          <Button
            type='button'
            onClick={onCancel}
            disabled={isRegenerating || isPublished}
            variant='text'
          >
            <Text variant='caption'>Cancel and return</Text>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RegeneratePostForm;
